import { useEffect, useState } from "react";
import {
  Paper,
  Typography,
  Button,
  Stack,
  Box,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { supabase } from "../supabaseClient";
import WishlistItems from "./WishlistItems";
import SurpriseItemDialog from "./SurpriseItemDialog";
import { exportWishlistItemsToCsv } from "../utils/exportToCsv";
import { useRenameName, useDeleteName } from "../hooks/useNames";
import GoHomeButton from "./GoHomeButton";

export default function NameDetails({ userId, nameId, isParent, isWishlistLocked, onNavigateHome }) {
  const [nameData, setNameData] = useState(null);
  const [childLists, setChildLists] = useState([]);
  const [editingName, setEditingName] = useState(false);
  const [editingText, setEditingText] = useState("");
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const renameNameMutation = useRenameName(nameData?.group_id);
  const deleteNameMutation = useDeleteName(nameData?.group_id);

  useEffect(() => {
    const fetchData = async () => {
      // Fetch name details
      const { data: name } = await supabase
        .from("names")
        .select("*")
        .eq("id", nameId)
        .single();

      setNameData(name);

      if (isParent && name && name.user_id !== userId) {
        const { data: kids } = await supabase
          .from("names")
          .select("id, name")
          .eq("group_id", name.group_id)
          .eq("user_id", userId)
          .eq("is_child_list", true)
          .order("name");
        setChildLists(kids || []);
      } else {
        setChildLists([]);
      }
    };

    fetchData();
  }, [nameId, isParent, userId]);

  if (!nameData) return <Typography>Loading...</Typography>;

  const isOwner = nameData.user_id === userId;
  const isChildList = !!nameData.is_child_list;

  const handleExport = async () => {
    const { data } = await supabase
      .from("wishlist_items")
      .select(`
        id,
        item_text,
        url,
        price,
        created_at,
        sort_order,
        claimed_by,
        claimed_at,
        names ( name ),
        users:claimed_by ( display_name )
      `)
      .eq("name_id", nameId)
      // Same ordering the on-screen list uses, so the export matches the ranking.
      .order("sort_order", { ascending: true, nullsFirst: false })
      .order("created_at", { ascending: false });

    if (!data) return;

    const filename = `${nameData.name}-wishlist.csv`;
    const totalCost = data.reduce((sum, i) => sum + Number(i.price || 0), 0);
    exportWishlistItemsToCsv(data, isOwner, filename, {
      includeRank: true,
      totalsMode: "total",
      totalCost,
    });
  };

  const saveEdit = async () => {
    try {
      const data = await renameNameMutation.mutateAsync({ id: nameId, newName: editingText });
      setNameData(data);
      setEditingName(false);
    } catch {
      // Error already logged in namesService; leave the edit field open so the user can retry.
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteNameMutation.mutateAsync({ id: nameId, name: nameData.name });
      setConfirmDeleteOpen(false);
      onNavigateHome();
    } catch {
      // Error already logged in namesService; leave the dialog open so the user can retry.
    }
  };

  return (
    <Paper elevation={2} className="page-card" sx={{ p: 3, mt: 2, mb: 4 }}>
      <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          {editingName ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
              <TextField
                value={editingText}
                size="small"
                onChange={(e) => setEditingText(e.target.value)}
              />
              <Button onClick={saveEdit}>Save</Button>
            </Stack>
          ) : (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Typography variant="h5" className="section-header">
                {nameData.name}
              </Typography>
              {isOwner && (
                <>
                  <IconButton
                    aria-label="edit"
                    size="small"
                    onClick={() => { setEditingName(true); setEditingText(nameData.name); }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    aria-label="delete"
                    size="small"
                    color="error"
                    onClick={() => setConfirmDeleteOpen(true)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </Stack>
          )}
          <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
            Created: {new Date(nameData.created_at).toLocaleString("en-US", {
              timeZone: "America/New_York"
            })}
          </Typography>
        </Box>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} width={{ xs: "100%", md: "auto" }}>
          <GoHomeButton onNavigateHome={onNavigateHome} />
          <SurpriseItemDialog userId={userId} groupId={nameData?.group_id} defaultRecipientNameId={nameId} />
          <Button variant="contained" onClick={handleExport} fullWidth={false}>
            Export to CSV
          </Button>
        </Stack>
      </Stack>

      <WishlistItems
        userId={userId}
        nameId={nameId}
        isOwner={isOwner}
        isChildList={isChildList}
        isParent={isParent}
        isWishlistLocked={isWishlistLocked}
        childLists={childLists}
      />

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete Name</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete "{nameData.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button color="error" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
